import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, notification } from "antd";
import { useHistory, useLocation, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useSelector } from "react-redux";
import { Breadcrumbs, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import styled from "styled-components";

import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useMutate } from "hooks/useMutate";
import { useQueryState } from "hooks/useQueryState";
import { getUserList, getFilteredClientData, getClientid } from "redux/selectors";
import COLORS from "common/Colors";

// ─── Label style for form fields ──────────────────────────────────────────────

const labelStyle = {
	width: "80px",
	display: "inline-block",
	textAlign: "start",
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ClientSurveyMatter = () => {
	const { id: engagementId } = useParams();
	const history = useHistory();
	const userList = useSelector(getUserList);
	const clientId = useSelector(getClientid);
	
	const location = useLocation();
	const passedMatterData = location?.state?.matterData;
	const engagementData = passedMatterData;

	// ── Breadcrumbs ────────────────────────────────────────────────────────────
	const breadStyle = { color: "#636980", cursor: "pointer" };
	const breadcrumbs = [
		<label key="1" style={breadStyle} onClick={() => history.goBack()}>
			{engagementData?.jobTitle || "Matter"}
		</label>,
		<Typography key="2" color="text.primary">
			Client Satisfaction Survey
		</Typography>,
	];

	return (
		<ClientSurveyWrap>
			{/* Header row */}
			<div className="title-row">
				<Breadcrumbs
					separator={<NavigateNextIcon fontSize="small" />}
					aria-label="breadcrumb"
				>
					{breadcrumbs}
				</Breadcrumbs>

				<div className="client">
					Client: {engagementData?.clientId || "-"}
				</div>
			</div>

			{/* Email form */}
			<ClientSurveyEmailForm
				engagementId={engagementId}
				engagementData={passedMatterData}
				userList={userList}
				clientId={clientId}
			/>
		</ClientSurveyWrap>
	);
};

export default ClientSurveyMatter;

// ─── Email Form Sub-component ─────────────────────────────────────────────────

const ClientSurveyEmailForm = ({ engagementId, engagementData, userList ,clientId}) => {
	const history = useHistory();
	const [form] = Form.useForm();
	const { addClientSurveyEmail: addClientSurveyEmailQuery } = queryKeys;
	const { key: emailKey, url: emailUrl } =
		addClientSurveyEmailQuery(engagementId);

	const { mutate: addClientSurveyEmail, isLoading } = useMutate(
		emailKey,
		emailUrl
	);

	const { getClient } = queryKeys;

	const { data: clientDataRes } = useFetch(
		getClient(engagementData?.clientId).key,
		getClient(engagementData?.clientId).url,
		{
			enabled: !!engagementData?.clientId,
		}
	);
	console.log("clientDataRes", clientDataRes);
	const clientData = clientDataRes?.response?.data;
	// ── Derived values ─────────────────────────────────────────────────────────

	// const emailaddress =
	//   filteredClientList?.data[0]?.emailaddress1 ??
	//   filteredClientList?.data[0]?.emailaddress2 ??
	//   filteredClientList?.data[0]?.emailaddress3 ??
	//   "";

	const findUser = (id) => userList?.find((u) => u?.userid === id);

	const assignedPartner = findUser(engagementData?.leadAttorney);
	const assignedPersonInCharge = findUser(engagementData?.referringAttorney);

	const managingPartnerName =
		assignedPartner
			? `${assignedPartner.firstname} ${assignedPartner.lastname}`
			: null;

	const personInChargeName =
		assignedPersonInCharge
			? `${assignedPersonInCharge.firstname} ${assignedPersonInCharge.lastname}`
			: null;

	const clientSurveyLink = "https://www.clientsurvey.legal.insyts.ai";

	const ccEmails = [
		engagementData?.stakeHolder?.partner,
		engagementData?.stakeHolder?.personIncharge,
	]
		.filter(Boolean)
		.join(",");

	useEffect(() => {
		if (engagementData) {
			form.setFieldsValue({
				title: engagementData?.jobTitle,
				subject: "Client Survey",
				to: [...new Set([clientData?.primarycontactemail, engagementData?.responsiblePersonnel?.map((p) => p.emailid)?.join(", ")]
					?.filter(Boolean))]
					?.join(", "),
				// cc: ccEmails,
				body: `Dear ${engagementData?.clientName},\n
    We are committed to high quality services to our clients. In that regard, I would request you to give me your feedback on how my team fared on the recently completed matter: ${engagementData?.jobTitle}.\n
	We request you to allocate 5 minutes to fill out this short client survey form using this link: ${clientSurveyLink}\n
	Thank you in advance for your support.
	`,
				footer: `Regards,\n${managingPartnerName},\nMatter Lead Attorney.`,
			});
		}
	}, [engagementData]);

	const handleSubmit = (values) => {
		const payload = {
			mailData: {
				title: values.title,
				subject: values.subject,
				toMail: values.to,
				ccMail: values.cc,
				body: values.body,
				footer: values.footer,
				clientsurveylink: clientSurveyLink,
				leadAttorney: managingPartnerName,
				referringAttorney: personInChargeName,
				mattername: engagementData?.jobTitle,
				clientId: engagementData?.clientId,
				clientname: engagementData?.clientName,
				userclientId:clientId
			},
		};

		addClientSurveyEmail(payload, {
			onSuccess: () => {
				notification.success({ message: "Email sent successfully" });
				// Navigate back to the matter detail page after sending
				history.push(`/matter-details/${engagementId}`);
			},
		});
	};

	// ── Render ─────────────────────────────────────────────────────────────────

	return (
		<Form
			form={form}
			onFinish={handleSubmit}
			layout="horizontal"
			colon={false}
		>
			<Card bordered={false}>
				<Form.Item
					label={<span style={labelStyle}>Title:</span>}
					name="title"
					rules={[{ required: true, message: "Title is required" }]}
				>
					<Input placeholder="Enter" disabled />
				</Form.Item>

				<Form.Item
					label={<span style={labelStyle}>Subject:</span>}
					name="subject"
					rules={[{ required: true, message: "Subject is required" }]}
				>
					<Input placeholder="Enter" />
				</Form.Item>

				<Form.Item
					label={<span style={labelStyle}>To:</span>}
					name="to"
					rules={[{ required: true, message: "Recipient email is required" }]}
				>
					<Input disabled placeholder="Enter" />
				</Form.Item>

				<Form.Item
					label={<span style={labelStyle}>CC:</span>}
					name="cc"
				>
					<Input placeholder="Enter" />
				</Form.Item>

				<Form.Item
					label={<span style={labelStyle}>Body:</span>}
					name="body"
					rules={[{ required: true, message: "Body is required" }]}
				>
					<Input.TextArea
						disabled
						rows={3}
						style={{ resize: "none" }}
					/>
				</Form.Item>

				<Form.Item
					label={<span style={labelStyle}>Footer:</span>}
					name="footer"
					rules={[{ required: true, message: "Footer is required" }]}
				>
					<Input.TextArea
						disabled
						rows={3}
						style={{ resize: "none" }}
					/>
				</Form.Item>
			</Card>

			<div className="form-buttons">
				<Button
					className="btn-color-close"
					onClick={() => history.push(`/matter-details/${engagementId}`)}
				>
					Close
				</Button>
				<Button
					className="btn-color"
					htmlType="submit"
					loading={isLoading}
					disabled={engagementData?.clientsurveystatus}
				>
					Send Mail
				</Button>
			</div>
		</Form>
	);
};

// ─── Styled Components ────────────────────────────────────────────────────────

const ClientSurveyWrap = styled.div`
  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 16px;

    .client {
      font-size: 16px;
      color: #1b1b1b;
      font-weight: 600;
    }
  }

  label {
    color: #1b1b1b;
  }

  .form-buttons {
    margin-top: 10px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
	.btn-color-close{
       background-color: rgb(255, 255, 255) !important;  
       color: #000000 !important; 
       border-color: #000000 !important;      
    }
    .btn-color{
       background-color: #00685F !important;  
       color: #ffffff !important;       
    }
    
  }

  .ant-card-body {
    box-shadow: 0px 3px 6px #00000015;
  }

  .ant-radio-group {
    display: flex;
    align-items: center;
    gap: 20px;

    label.ant-radio-button-wrapper {
      width: 100px;
      text-align: center;
      background-color: #f7f7f8;

      &:hover {
        box-shadow: #00000015 0px 3px 6px;
      }

      .ant-radio-button-checked {
        background-color: ${COLORS.PRIMARY};
        color: black !important;
      }

      color: black !important;
      border: none;
    }

    .ant-radio-button-wrapper:not(:first-child)::before {
      display: none;
    }
  }
`;